/**
 * The signed-in user's job role, as free text.
 *
 * This is deliberately NOT `realm_access.roles`. Those are access-control roles
 * with a fixed vocabulary; this is a human job description typed into a text
 * field on the Keycloak user ("CEO Focke", "production leader factory XY",
 * "machine operator line 3") and mapped into the access token as a custom claim.
 *
 * Because it is free text there is nothing to map it onto — it is passed
 * verbatim to the AI agent, which decides what level of detail suits it. When no
 * role is set we ask for a generalised summary instead.
 */

import { decodeToken } from "./tokenUtils";

/**
 * Claim names to look for, in order. The attribute→claim mapping is configured
 * in Keycloak and we don't control its name, so we probe the plausible ones
 * rather than hard-coding a guess. German spellings are included because the
 * rest of this deployment's data is German.
 *
 * `VITE_USER_ROLE_CLAIM` overrides this entirely (comma-separated for several).
 */
const DEFAULT_CLAIM_CANDIDATES = [
  // The name actually configured on the Keycloak user in this deployment.
  "companyRole",
  "company_role",
  "role",
  "user_role",
  "userRole",
  "job_role",
  "jobRole",
  "jobTitle",
  "job_title",
  "position",
  "function",
  "funktion",
  "rolle",
] as const;

/**
 * Claims that are never a job role, so the dev-only discovery helper below
 * doesn't suggest them. `name` in particular is the user's full name and would
 * look plausible to a reader scanning output.
 */
const NON_ROLE_CLAIMS = new Set([
  "sub",
  "iss",
  "aud",
  "jti",
  "typ",
  "azp",
  "nonce",
  "sid",
  "acr",
  "scope",
  "name",
  "given_name",
  "family_name",
  "middle_name",
  "nickname",
  "email",
  "preferred_username",
  "picture",
  "locale",
  "session_state",
  "at_hash",
  "c_hash",
  "auth_time",
  "updated_at",
  "email_verified",
  // Access-control data, not a job description. `realm_access.roles` in
  // particular is a fixed vocabulary and explicitly not what we want here.
  "realm_access",
  "resource_access",
  "roles",
  "groups",
  "organization",
  // Keycloak boilerplate that is string- or array-valued and would otherwise
  // show up as a plausible-looking candidate.
  "allowed-origins",
]);

/**
 * Claim names that look like a job description.
 *
 * Backstop for the exact-match list: the attribute name is chosen in Keycloak,
 * not here, and we already guessed wrong once — `companyRole` was configured
 * while we probed eleven other spellings. Anything whose name mentions a role,
 * position or job is worth trying before giving up.
 */
const ROLE_NAME_HINT = /(role|rolle|position|funktion|job|title|function)/i;

function configuredCandidates(): readonly string[] {
  const configured = import.meta.env.VITE_USER_ROLE_CLAIM as string | undefined;
  if (!configured) return DEFAULT_CLAIM_CANDIDATES;
  const names = configured
    .split(",")
    .map((n) => n.trim())
    .filter(Boolean);
  return names.length > 0 ? names : DEFAULT_CLAIM_CANDIDATES;
}

/**
 * Coerce a claim value to a trimmed role string.
 *
 * Keycloak user attributes come through as a string, but a multivalued
 * attribute arrives as an array — hence the join rather than a String() cast,
 * which would produce "[object Object]" or a bare comma-joined blob.
 */
function toRoleString(value: unknown): string | null {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  if (Array.isArray(value)) {
    const parts = value
      .filter((v): v is string => typeof v === "string")
      .map((v) => v.trim())
      .filter((v) => v.length > 0);
    return parts.length > 0 ? parts.join(", ") : null;
  }
  return null;
}

export interface ResolvedUserRole {
  /** The role exactly as configured in Keycloak, e.g. "production leader factory XY". */
  role: string;
  /** Which claim it came from — useful when confirming the Keycloak mapping. */
  claim: string;
}

/**
 * Pull the free-text role out of already-decoded token claims.
 *
 * Returns `null` when no role is configured for the user, which is a normal
 * case: the summary then falls back to a general briefing.
 */
export function resolveUserRoleFromClaims(
  claims: Record<string, unknown> | null | undefined,
): ResolvedUserRole | null {
  if (!claims) return null;

  // Exact matches first, so an explicitly configured name always wins.
  for (const claim of configuredCandidates()) {
    const role = toRoleString(claims[claim]);
    if (role) return { role, claim };
  }

  // Then anything whose name reads like a job description. This is what makes a
  // new attribute name work without a code change.
  for (const [claim, raw] of Object.entries(claims)) {
    if (NON_ROLE_CLAIMS.has(claim)) continue;
    if (!ROLE_NAME_HINT.test(claim)) continue;
    const role = toRoleString(raw);
    if (role) return { role, claim };
  }

  return null;
}

/** Pull the free-text role out of a raw JWT. */
export function resolveUserRoleFromToken(
  token: string | null | undefined,
): ResolvedUserRole | null {
  if (!token) return null;
  const claims = decodeToken(token) as Record<string, unknown> | null;
  return resolveUserRoleFromClaims(claims);
}

/**
 * Dev-only aid: every string-valued claim that isn't a standard OIDC field.
 *
 * The role attribute's claim name is configured in Keycloak, not here, so if
 * none of the candidates match this is what tells us what to add.
 */
export function discoverCandidateRoleClaims(
  claims: Record<string, unknown> | null | undefined,
): Array<{ claim: string; value: string }> {
  if (!claims) return [];
  const found: Array<{ claim: string; value: string }> = [];
  for (const [claim, raw] of Object.entries(claims)) {
    if (NON_ROLE_CLAIMS.has(claim)) continue;
    const value = toRoleString(raw);
    if (value) found.push({ claim, value });
  }
  return found;
}
